import React, { useState, useEffect, useContext } from "react"
import { graphql } from "gatsby"
import { kebabCase } from "lodash"

import CustomHtmlPopUp from "../popups/CustomHtmlPopup"
import ImageTextPopup from "../popups/ImageTextPopup"
import { PopupsContext } from "../../contexts/PopupsContext"

const getPopupId = (popup, startSlicingAtChar = 25) => {
  const { __component, __typename, strapi_id } = popup
  if (__component) return `${__component}.${strapi_id}`
  if (__typename)
    return `popups.${kebabCase(
      __typename.slice(startSlicingAtChar)
    )}.${strapi_id}`
  // slice(25) removes "STRAPI__COMPONENT_POPUPS_"
  else return ""
}
const Popups = ({
  site,
  popup,
  popupId,
  onClose,
  modalStyling,
  className,
  isModalOpen,
  setModal,
  isHomepage,
}) => {
  const { popups } = site ?? {}
  const { openModalSectionById } = useContext(PopupsContext)
  const [activePopup, setActivePopup] = useState(null)

  const onCloseHandler = () => {
    if (setModal) setModal(false)
    if (onClose) onClose()

    // Unset page background scrolling when popup is closed
    document.body.style.overflow = "unset"
  }

  if (isModalOpen && activePopup) {
    //Disables page background scrolling while the modal is open
    if (typeof window != "undefined" && window.document) {
      document.body.style.overflow = "hidden"
    }
  }

  useEffect(() => {
    let timeoutId
    if (isHomepage) {
      // revealing timed popup on homepage
      popups?.forEach(popup => {
        const { sizeSelections, customSize } = popup?.popup?.timerDelay ?? {}
        const popupId = getPopupId(popup)
        const hasPopupOpened = Boolean(sessionStorage.getItem(popupId))

        if (sizeSelections === "custom" && customSize > -1 && !hasPopupOpened) {
          const timer = customSize * 1000

          timeoutId = setTimeout(() => {
            openModalSectionById(popupId)
            const activePopupActive = getPopupId(popup)
            const hasModalOpened = activePopupActive === popupId
            sessionStorage.setItem(popupId, hasModalOpened) // remember that this popup has been opened
          }, timer)
        }
      })
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId) // cleanup function that cancels the timeout when the component is unmounted
    }
  }, [])

  useEffect(() => {
    if (!popup) {
      const currentPopup = popups?.filter(popup => {
        return popup.strapi_id === popupId || getPopupId(popup, 25) === popupId
      })[0]

      setActivePopup(currentPopup)
    } else {
      if (popup !== activePopup) {
        setActivePopup(popup)
      }
    }
    if (setModal) {
      setModal(true) // setting isOpen indicator. this is used to style page on gatsby sites
      return () => setModal(false)
    }
  }, [popupId])

  const renderedPopup = popup => {
    if (!popup?.strapi_id && !popup?.id) return null
    const modalProps = {
      isOpen: isModalOpen,
      close: onCloseHandler,
      ...modalStyling,
    }
    switch (popup?.__typename || popup?.__component) {
      case "STRAPI__COMPONENT_POPUPS_CUSTOM_HTML":
      case "popups.custom-html":
        return (
          <CustomHtmlPopUp
            className="CustomHtmlPopUp"
            {...popup}
            modalProps={modalProps}
          />
        )
      case "popups.image-text-popup":
      case "STRAPI__COMPONENT_POPUPS_IMAGE_TEXT_POPUP":
        return (
          <ImageTextPopup
            className={className}
            {...popup}
            modalProps={modalProps}
          />
        )
      default:
        return null
    }
  }

  return renderedPopup(popup ? popup : activePopup) //this render update on morphic app but I want to find a better way
}

export default Popups

export var query = graphql`
  fragment Popups on SitePopupsDynamicZone {
    __typename
    ... on STRAPI__COMPONENT_POPUPS_CUSTOM_HTML {
      ...CustomHtmlPopup
    }
    ... on STRAPI__COMPONENT_POPUPS_IMAGE_TEXT_POPUP {
      ...ImageTextPopup
      align: alignProp
    }
  }
`
